import { Stack, Text } from '@mantine/core';
import React, { FC, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { usePaginatedAuditLogs } from '@portals/api/ui';
import { DateCell, PaginatedTable } from '@portals/table';
import {
  TableColumn,
  TableFilterTypeEnum,
  AuditLogTemplates,
  AuditLogType,
} from '@portals/types';

export interface AuditLogsProps {
  messagesList: Partial<
    Record<AuditLogTemplates, (auditLog: AuditLogType) => ReactNode>
  >;
  getUserRoute: (userId: string) => string;
}

export const AuditLogs: FC<AuditLogsProps> = ({
  messagesList,
  getUserRoute,
}) => {
  const columns: TableColumn<AuditLogType>[] = [
    {
      dataField: 'user_name',
      text: 'Name',
      sort: true,
      filter: { type: TableFilterTypeEnum.Text },
      maxWidth: 50,
      formatter: (_, { user }) => {
        if (!user) return '--';

        return (
          <Stack spacing="xs">
            <Text<typeof Link>
              component={Link}
              to={getUserRoute(user.id)}
              size="sm"
              color="blue"
              underline
            >
              {user.name}
            </Text>
            {user.email && <Text size="xs">({user.email})</Text>}
          </Stack>
        );
      },
    },
    {
      dataField: 'full_message',
      text: 'Message',
      sort: true,
      filter: { type: TableFilterTypeEnum.Text },
      formatter: (cell, row) => {
        const messageRenderer = messagesList[row.template];

        if (messageRenderer && typeof messageRenderer === 'function') {
          return messageRenderer(row);
        } else {
          return row.full_message;
        }
      },
    },
    {
      dataField: 'created_at',
      text: 'Date',
      sort: true,
      maxWidth: 50,
      filter: { type: TableFilterTypeEnum.Date },
      formatter: (cell, { created_at }) => <DateCell date={created_at} />,
    },
  ];

  return (
    <PaginatedTable<AuditLogType>
      keyField="id"
      noHeader
      name="auditLogs"
      columns={columns}
      dataHook={usePaginatedAuditLogs}
      noDataIndication={{ title: 'No audit logs to display' }}
      defaultSortBy={[{ id: 'created_at', desc: true }]}
    />
  );
};
